// src/components/application/link_lists/LinkListsPage.js
import React from "react";
import {
    Paper, TextField, TableContainer, Table, TableHead, TableRow,
    TableCell, TableBody, Button, Box, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, TableSortLabel,
    IconButton, Tooltip, Switch, FormControlLabel, Chip, Stack
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PersonIcon from "@mui/icons-material/Person";
import PageContainer from "../layout/PageContainer";
import useLinkLists from "../../hooks/useLinkLists";

function isAiTrueValue(v) {
    // Normalize backend values (bool/int/"0"/"1"/null) to boolean.
    if (v === true) return true;
    if (v === false) return false;
    if (v == null) return false;

    if (typeof v === "number") return v === 1;
    if (typeof v === "string") {
        const s = v.trim().toLowerCase();
        return s === "1" || s === "true" || s === "yes" || s === "on";
    }
    return !!v;
}

function getAiRating(row) {
    // Accept different spellings (snake_case, camelCase, german keys).
    const v =
        row?.ai_rating ??
        row?.aiRating ??
        row?.ki_bewertung ??
        row?.kiBewertung ??
        row?.ki_rating ??
        row?.kiRating ??
        row?.ai ??
        row?.ai_flag ??
        null;
    return isAiTrueValue(v);
}

function getAiReason(row) {
    // Accept different spellings (snake_case, camelCase, german keys).
    return (
        row?.ai_reason ??
        row?.aiReason ??
        row?.ki_grund ??
        row?.kiGrund ??
        row?.ki_reason ??
        row?.kiReason ??
        ""
    );
}

function getAiProbability(row) {
    // Accept different spellings (snake_case, camelCase, german keys).
    return (
        row?.ai_probability ??
        row?.aiProbability ??
        row?.wahrscheinlichkeit ??
        row?.Wahrscheinlichkeit ??
        row?.probability ??
        null
    );
}

// ---- Sort-Utils (stabil, Strings case-insensitiv) ----
function getCellValue(row, key) {
    switch (key) {
        case "url": return row.url ?? "";
        case "is_active": return row.is_active ? 1 : 0;
        case "created_at": return row.created_at ?? "";
        case "created_by": return row.created_by ?? 0;
        case "tags": return Array.isArray(row.tags) ? row.tags.join(",") : "";
        case "ai_rating": return getAiRating(row) ? 1 : 0;
        case "ai_reason": return getAiReason(row) ?? "";
        case "ai_probability": return getAiProbability(row) ?? -1;
        default: return "";
    }
}
function descendingComparator(a, b, orderBy) {
    const va = getCellValue(a, orderBy);
    const vb = getCellValue(b, orderBy);

    // Numeric compare for booleans / ids, string compare otherwise.
    const na = Number(va);
    const nb = Number(vb);
    const bothNumeric =
        Number.isFinite(na) &&
        Number.isFinite(nb) &&
        typeof va !== "string" &&
        typeof vb !== "string";

    if (bothNumeric) {
        if (nb < na) return -1;
        if (nb > na) return 1;
        return 0;
    }

    const sa = (va ?? "").toString().toLowerCase();
    const sb = (vb ?? "").toString().toLowerCase();
    if (sb < sa) return -1;
    if (sb > sa) return 1;
    return 0;
}
function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
    const stabilized = array.map((el, idx) => [el, idx]);
    stabilized.sort((a, b) => {
        const res = comparator(a[0], b[0]);
        if (res !== 0) return res;
        return a[1] - b[1];
    });
    return stabilized.map(el => el[0]);
}

function parseTagsInput(s) {
    // Accept comma-separated tags and normalize.
    return (s || "")
        .split(",")
        .map(t => t.trim())
        .filter(Boolean);
}

export default function LinkListsPage() {
    // TODO: Replace with real user id from your auth/session context
    const currentUserId = 1;

    const {
        items,
        loading,
        error,
        fetchLinkLists,
        createLinkList,
        updateLinkList,
        toggleLinkList,
        deleteLinkList,
    } = useLinkLists({ currentUserId });

    // Sort state
    const [order, setOrder] = React.useState("desc");
    const [orderBy, setOrderBy] = React.useState("created_at");

    // Filters
    const [q, setQ] = React.useState("");
    const [onlyActive, setOnlyActive] = React.useState(false);

    // Create/Edit dialog
    const [open, setOpen] = React.useState(false);
    const [editingId, setEditingId] = React.useState(null);
    const [formUrl, setFormUrl] = React.useState("");
    const [formTags, setFormTags] = React.useState(""); // comma-separated
    const [formAiRating, setFormAiRating] = React.useState(false);
    const [formAiReason, setFormAiReason] = React.useState("");
    const [formAiProbability, setFormAiProbability] = React.useState("");
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState("");

    // Delete confirm
    const [delOpen, setDelOpen] = React.useState(false);
    const [delId, setDelId] = React.useState(null);
    const [delUrl, setDelUrl] = React.useState("");
    const [delSubmitting, setDelSubmitting] = React.useState(false);
    const [delError, setDelError] = React.useState("");

    const handleRequestSort = (property) => () => {
        setOrder(prev => (orderBy === property && prev === "asc" ? "desc" : "asc"));
        setOrderBy(property);
    };

    const handleOpenCreate = () => {
        setSubmitError("");
        setEditingId(null);
        setFormUrl("");
        setFormTags("");
        setFormAiRating(false);
        setFormAiReason("");
        setFormAiProbability("");
        setOpen(true);
    };

    const handleOpenEdit = (row) => {
        setSubmitError("");
        setEditingId(row.id);
        setFormUrl(row.url || "");
        setFormTags(Array.isArray(row.tags) ? row.tags.join(", ") : "");
        setFormAiRating(getAiRating(row));
        setFormAiReason(getAiReason(row) || "");
        const p = getAiProbability(row);
        setFormAiProbability(p == null || p === "" ? "" : String(p));
        setOpen(true);
    };

    const handleSubmit = async () => {
        setSubmitError("");
        const url = (formUrl || "").trim();
        if (!url) { setSubmitError("URL ist Pflichtfeld."); return; }

        const tags = parseTagsInput(formTags);
        const probStr = (formAiProbability || "").trim();
        const prob = probStr === "" ? null : Number(probStr);

        setSubmitting(true);
        try {
            const payload = {
                url,
                tags,
                ai_rating: !!formAiRating,
                ai_reason: (formAiReason || "").trim() ? (formAiReason || "").trim() : null,
                ai_probability: prob,
            };

            if (editingId) {
                await updateLinkList(editingId, payload);
            } else {
                await createLinkList(url, tags, {
                    ai_rating: payload.ai_rating,
                    ai_reason: payload.ai_reason,
                    ai_probability: payload.ai_probability,
                });
            }

            setOpen(false);
            setEditingId(null);
            setFormUrl("");
            setFormTags("");
            setFormAiRating(false);
            setFormAiReason("");
            setFormAiProbability("");
        } catch (e) {
            setSubmitError(e.message || "Speichern fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenDelete = (row) => {
        setDelError("");
        setDelId(row.id);
        setDelUrl(row.url || "");
        setDelOpen(true);
    };

    const handleConfirmDelete = async () => {
        setDelError("");
        setDelSubmitting(true);
        try {
            await deleteLinkList(delId);
            setDelOpen(false);
            setDelId(null);
            setDelUrl("");
        } catch (e) {
            setDelError(e.message || "Löschen fehlgeschlagen.");
        } finally {
            setDelSubmitting(false);
        }
    };

    const filteredRows = React.useMemo(() => {
        const list = Array.isArray(items) ? items : [];
        const needle = (q || "").toLowerCase();

        const filtered = list.filter((r) => {
            if (onlyActive && !r.is_active) return false;
            if (!needle) return true;

            const u = (r.url ?? "").toString().toLowerCase();
            const t = (Array.isArray(r.tags) ? r.tags.join(" ") : "").toLowerCase();
            const ar = (getAiReason(r) || "").toString().toLowerCase();

            return u.includes(needle) || t.includes(needle) || ar.includes(needle);
        });

        return stableSort(filtered, getComparator(order, orderBy));
    }, [items, q, onlyActive, order, orderBy]);

    const formatProbability = (value) => {
        if (value == null || value === "") return "—";
        const n = Number(value);
        if (Number.isNaN(n)) return String(value);
        return n.toFixed(4);
    };

    const columns = [
        { id: "url", label: "URL" },
        { id: "tags", label: "Tags" },
        { id: "is_active", label: "Aktiv" },
        { id: "ai_rating", label: "KI Bewertung" },
        { id: "ai_reason", label: "KI Grund" },
        { id: "ai_probability", label: "Wahrscheinlichkeit" },
        { id: "created_at", label: "Hinzugefügt" },
        { id: "created_by", label: "User" },
        { id: "actions", label: "" },
    ];

    return (
        <PageContainer
            title="Link Verzeichnisse"
            breadcrumbs={["Home", "Link Verzeichnisse"]}
            actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={fetchLinkLists} disabled={loading}>Aktualisieren</Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>Neue URL</Button>
                </Box>
            }
        >
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Suche */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <TextField
                    placeholder="Suchen... (z. B. example.com)"
                    fullWidth
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
                <Box sx={{ mt: 1 }}>
                    <FormControlLabel
                        control={<Switch checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} />}
                        label="Nur aktive"
                    />
                </Box>
            </Paper>

            <TableContainer component={Paper}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns.map(col => (
                                    <TableCell
                                        key={col.id}
                                        sortDirection={orderBy === col.id ? order : false}
                                        sx={{ userSelect: "none" }}
                                    >
                                        {col.id === "actions" ? null : (
                                            <TableSortLabel
                                                active={orderBy === col.id}
                                                direction={orderBy === col.id ? order : "asc"}
                                                onClick={handleRequestSort(col.id)}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredRows.map((row) => {
                                const ai = getAiRating(row);
                                const reason = getAiReason(row);
                                const prob = getAiProbability(row);

                                return (
                                    <TableRow key={row.id} hover>
                                        <TableCell sx={{ wordBreak: "break-all" }}>{row.url}</TableCell>

                                        <TableCell sx={{ minWidth: 260 }}>
                                            {Array.isArray(row.tags) && row.tags.length ? (
                                                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                                    {row.tags.slice(0, 8).map((t) => (
                                                        <Chip key={t} size="small" label={t} />
                                                    ))}
                                                    {row.tags.length > 8 && (
                                                        <Chip size="small" variant="outlined" label={`+${row.tags.length - 8}`} />
                                                    )}
                                                </Stack>
                                            ) : (
                                                <em>-</em>
                                            )}
                                        </TableCell>

                                        <TableCell>
                                            <Switch
                                                checked={!!row.is_active}
                                                onChange={() => toggleLinkList(row)}
                                                size="small"
                                            />
                                        </TableCell>

                                        <TableCell>
                                            {ai ? (
                                                <Chip
                                                    icon={<AutoAwesomeIcon />}
                                                    label="gemini"
                                                    variant="outlined"
                                                    sx={{
                                                        fontFamily: "monospace",
                                                        fontWeight: 500,
                                                        background: "linear-gradient(90deg,#1a73e8,#9334e6)",
                                                        color: "white",
                                                        border: "none",
                                                        "& .MuiChip-icon": { color: "white" },
                                                    }}
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<PersonIcon />}
                                                    label="human"
                                                    variant="outlined"
                                                    sx={{ fontFamily: "monospace", fontWeight: 500 }}
                                                />
                                            )}
                                        </TableCell>

                                        <TableCell sx={{ minWidth: 260 }}>
                                            {reason ? (
                                                <span style={{ whiteSpace: "pre-wrap" }}>{reason}</span>
                                            ) : (
                                                <em>-</em>
                                            )}
                                        </TableCell>

                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {formatProbability(prob)}
                                        </TableCell>

                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {row.created_at ? String(row.created_at) : <em>-</em>}
                                        </TableCell>

                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {row.created_by ?? <em>-</em>}
                                        </TableCell>

                                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                            <Tooltip title="Bearbeiten">
                                                <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Löschen">
                                                <IconButton size="small" onClick={() => handleOpenDelete(row)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}

                            {!filteredRows.length && !loading && (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                                        Keine Einträge gefunden.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {/* Create/Edit Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editingId ? "Link-Liste bearbeiten" : "Neue Link-Liste URL"}</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
                        <TextField
                            autoFocus
                            label="URL*"
                            value={formUrl}
                            onChange={(e) => setFormUrl(e.target.value)}
                            placeholder="https://example.com/irgendwas"
                        />
                        <TextField
                            label="Tags (kommagetrennt)"
                            value={formTags}
                            onChange={(e) => setFormTags(e.target.value)}
                            placeholder="seo, partner, verzeichnis"
                            helperText="Mehrere Tags mit Komma trennen."
                        />

                        <FormControlLabel
                            control={<Switch checked={!!formAiRating} onChange={(e) => setFormAiRating(e.target.checked)} />}
                            label="KI Bewertung"
                        />

                        <TextField
                            label="KI Grund"
                            value={formAiReason}
                            onChange={(e) => setFormAiReason(e.target.value)}
                            placeholder="Warum KI das so bewertet hat…"
                        />

                        <TextField
                            label="Wahrscheinlichkeit"
                            value={formAiProbability}
                            onChange={(e) => setFormAiProbability(e.target.value)}
                            inputProps={{ inputMode: "decimal" }}
                            placeholder="z. B. 0.8734"
                        />

                        {!!submitError && <Alert severity="error">{submitError}</Alert>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={submitting}>Abbrechen</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={submitting || !formUrl.trim()}>
                        {submitting ? "Speichern…" : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirm Dialog */}
            <Dialog open={delOpen} onClose={() => setDelOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Eintrag löschen</DialogTitle>
                <DialogContent dividers>
                    <Alert severity="warning">
                        Willst du diese URL wirklich löschen?
                        <br />
                        <b style={{ wordBreak: "break-all" }}>{delUrl}</b>
                    </Alert>
                    {!!delError && <Alert severity="error" sx={{ mt: 2 }}>{delError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDelOpen(false)} disabled={delSubmitting}>Abbrechen</Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error" disabled={delSubmitting}>
                        {delSubmitting ? "Löschen…" : "Löschen"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
